import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useAuth } from "../../authContext/authContext";
import { submitOCRScan } from "../../Services/apiService";
import "bootstrap/dist/css/bootstrap.min.css";

function OCR() {
  const { user: currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [testId, setTestId] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [scannedImage, setScannedImage] = useState(null);
  const [showButtons, setShowButtons] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingType, setLoadingType] = useState(""); // "camera", "submit", ""
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  // Camera selection states
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [showCameraSelector, setShowCameraSelector] = useState(false);

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info"); // "success", "error", "warning", "info"

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Initialize component on mount
  useEffect(() => {
    // Detect mobile device
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      return /android|ipad|iphone|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent.toLowerCase()
      );
    };
    setIsMobile(checkMobile());

    // Enumerate available cameras
    const enumerateCameras = async () => {
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          let videoDevices = devices.filter(
            (device) => device.kind === "videoinput"
          );

          // Filter to only rear cameras on mobile devices
          if (checkMobile()) {
            videoDevices = videoDevices.filter(
              (device) =>
                device.label.toLowerCase().includes("back") ||
                device.label.toLowerCase().includes("rear") ||
                device.label.toLowerCase().includes("environment") ||
                device.facingMode === "environment"
            );
          }

          console.log("Available cameras:", videoDevices);
          setAvailableCameras(videoDevices);

          // Set default camera (prefer back camera for mobile)
          if (videoDevices.length > 0) {
            const backCamera = videoDevices.find(
              (device) =>
                device.label.toLowerCase().includes("back") ||
                device.label.toLowerCase().includes("rear") ||
                device.label.toLowerCase().includes("environment")
            );
            setSelectedCameraId(
              backCamera ? backCamera.deviceId : videoDevices[0].deviceId
            );
          }
        }
      } catch (error) {
        console.error("Error enumerating cameras:", error);
      }
    };

    enumerateCameras();

    // Cleanup function
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Authentication check function
  const checkAuthenticationAndStart = () => {
    startCamera();
  };

  // Snackbar helper functions
  const showSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // Check authentication when component loads
  useEffect(() => {
    if (!authLoading && !currentUser) {
      showSnackbar("Please login to access the OCR scanner.", "warning");
    }
  }, [authLoading, currentUser]);

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      setError("");
      setLoading(true);
      setLoadingType("camera");

      // First, ensure we're in scanning mode so the video element is rendered
      setIsScanning(true);

      // Wait a moment for React to render the video element
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Check if browser supports camera
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Camera not supported by your browser");
      }

      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      // Camera constraints - use selected camera or fallback to facingMode
      const constraints = {
        video: selectedCameraId
          ? {
              deviceId: { exact: selectedCameraId },
              width: { ideal: 1280, min: 640 },
              height: { ideal: 720, min: 480 },
            }
          : {
              facingMode: isMobile ? "environment" : "user", // Back camera for mobile, front for desktop
              width: { ideal: 1280, min: 640 },
              height: { ideal: 720, min: 480 },
            },
      };

      console.log("Requesting camera with constraints:", constraints);

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Wait for video element to be available
      if (!videoRef.current) {
        console.log("Video element not found, waiting...");
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      if (videoRef.current) {
        console.log("Video element found, setting up stream");
        videoRef.current.srcObject = stream;

        // Add event listeners to handle video loading
        videoRef.current.onloadedmetadata = () => {
          console.log("Video metadata loaded");
          if (videoRef.current) {
            videoRef.current
              .play()
              .then(() => {
                console.log("Video started playing successfully");
                setLoading(false);
                setLoadingType("");
              })
              .catch((e) => {
                console.error("Error playing video:", e);
                showSnackbar(
                  "Failed to start video playback. Please try again.",
                  "error"
                );
                setLoading(false);
                setLoadingType("");
                setIsScanning(false);
              });
          }
        };

        videoRef.current.oncanplay = () => {
          console.log("Video can start playing");
          setLoading(false);
          setLoadingType("");
        };

        videoRef.current.onerror = (e) => {
          console.error("Video error:", e);
          showSnackbar(
            "Video playback error. Please check your camera and try again.",
            "error"
          );
          setLoading(false);
          setLoadingType("");
          setIsScanning(false);
        };

        // Set video properties for mobile compatibility
        videoRef.current.playsInline = true;
        videoRef.current.muted = true;
        videoRef.current.autoplay = true;
      } else {
        console.error("Video element still not found after waiting");
        showSnackbar(
          "Video element not found. Please refresh the page and try again.",
          "error"
        );
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
        setIsScanning(false);
        setLoading(false);
        setLoadingType("");
        return;
      }
    } catch (err) {
      console.error("Error accessing camera:", err);

      let errorMessage = "Unable to access camera";
      if (err.name === "NotAllowedError") {
        errorMessage =
          "Camera permission denied. Please allow camera access and refresh the page.";
      } else if (err.name === "NotFoundError") {
        errorMessage = "No camera found on this device.";
      } else if (err.name === "NotSupportedError") {
        errorMessage = "Camera not supported on this device/browser.";
      } else if (err.name === "NotReadableError") {
        errorMessage =
          "Camera is already in use by another application. Please close other camera apps and try again.";
      }

      showSnackbar(errorMessage, "error");
      setIsScanning(false);
      setLoading(false);
      setLoadingType("");
    }
  }, [isMobile, selectedCameraId]);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsScanning(false);
  }, []);

  // Handle back button click
  const handleBackClick = () => {
    stopCamera(); // Stop camera and cleanup
    navigate("/dashboard"); // Navigate to dashboard
  };

  // Capture image - no mirroring/flipping
  const captureImage = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      // Set canvas size to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas (no transformation - exact image)
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Get image data URL
      const imageDataUrl = canvas.toDataURL("image/jpeg", 2);

      setScannedImage(imageDataUrl);
      setShowButtons(true);
      stopCamera();
    }
  }, [stopCamera]);

  // Retry scan - use existing stream if available
  const retryScanning = useCallback(() => {
    setScannedImage(null);
    setShowButtons(false);
    setError("");
    setSuccess("");
    setSnackbarOpen(false); // Close any open snackbar

    // If we have an existing stream, just resume scanning
    if (streamRef.current && videoRef.current) {
      console.log("Resuming existing camera stream");
      setIsScanning(true);
      setLoading(false);
    } else {
      // Otherwise start fresh
      startCamera();
    }
  }, [startCamera]);

  // Submit scan
  const submitScan = useCallback(async () => {
    if (!testId.trim()) {
      showSnackbar("Please enter a Test ID before submitting.", "warning");
      return;
    }

    if (!scannedImage) {
      showSnackbar("Please capture an image before submitting.", "warning");
      return;
    }

    try {
      setLoading(true);
      setLoadingType("submit");
      setError("");

      console.log("📤 Submitting OCR scan...");
      console.log("🔗 Test ID:", testId);
      console.log("📷 Image captured:", scannedImage ? "Yes" : "No");

      // Call the API service to submit the OCR scan
      const result = await submitOCRScan(testId.trim(), scannedImage);

      console.log("✅ OCR submission successful:", result);
      showSnackbar(
        `OCR scan submitted successfully for Test ID: ${testId}`,
        "success"
      );

      // Reset form and automatically start camera for next scan
      setTestId("");
      setScannedImage(null);
      setShowButtons(false);

      // Wait a moment then restart camera for next scan
      setTimeout(() => {
        startCamera();
      }, 1000);
    } catch (err) {
      console.error("❌ OCR submission error:", err);

      // Handle different error types
      let errorMessage = "Failed to submit OCR scan. Please try again.";

      if (err.message.includes("No user logged in")) {
        errorMessage = "Authentication required. Please log in again.";
      } else if (err.message.includes("HTTP error")) {
        errorMessage = `Server error: ${err.message}`;
      } else if (err.message) {
        errorMessage = err.message;
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
      setLoadingType("");
    }
  }, [testId, scannedImage, startCamera]);

  // Handle file upload
  const handleFileUpload = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (!file) return;

      // Check if file is an image
      if (!file.type.startsWith("image/")) {
        showSnackbar(
          "Please select a valid image file (JPG, PNG, etc.)",
          "error"
        );
        return;
      }

      // Check file size (limit to 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        showSnackbar(
          "File size too large. Please select an image smaller than 10MB.",
          "error"
        );
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageDataUrl = e.target.result;
        setScannedImage(imageDataUrl);
        setShowButtons(true);
        stopCamera(); // Stop camera if it's running
        showSnackbar("Image uploaded successfully!", "success");
      };
      reader.onerror = () => {
        showSnackbar(
          "Error reading the selected file. Please try again.",
          "error"
        );
      };
      reader.readAsDataURL(file);

      // Reset file input
      event.target.value = "";
    },
    [stopCamera]
  );

  // Trigger file upload
  const triggerFileUpload = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        padding: 2,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Top Bar with Back and Title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 2,
          mb: 3,
          px: 2, // side padding
          flexWrap: "wrap", // wraps nicely for smaller screens
        }}
      >
        {/* Back Button */}
        <Button
          onClick={handleBackClick}
          startIcon={<i className="bi bi-arrow-left"></i>}
          variant="outlined"
          sx={{
            color: "#6c757d",
            borderColor: "#6c757d",
            "&:hover": {
              borderColor: "#5a6268",
              backgroundColor: "rgba(108, 117, 125, 0.1)",
            },
            minWidth: { xs: "40px", sm: "auto" }, // smaller button on mobile
            // px: { xs: 1, sm: 2 }, // tighter padding on mobile
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Back to Dashboard
          </Box>
        </Button>

        {/* OCR Scanner Title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            color: "#0058a5",
            textAlign: "center",
            flexGrow: 1,
          }}
        >
          Result Scanner
        </Typography>
      </Box>

      {/* Subtitle */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: "center" }}
      >
        Enter Test ID and scan documents
      </Typography>

      {/* User Status Indicator */}
      {!authLoading && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          {currentUser ? (
            <span className="badge bg-success">
              <i className="bi bi-person-check me-1"></i>
              Signed in as {currentUser.email}
            </span>
          ) : (
            <span className="badge bg-warning text-dark">
              <i className="bi bi-person-x me-1"></i>
              Not signed in
            </span>
          )}
        </Box>
      )}

      {/* Test ID + Camera Selection Row */}
<Box
  sx={{
    display: { xs: "flex", sm: "block" }, // row on mobile, stacked on desktop
    gap: 2,
    mb: 3,
    mt: 2,
  }}
>
  {/* Test ID Input */}
  <TextField
    fullWidth
    label="Test ID"
    value={testId}
    onChange={(e) => setTestId(e.target.value)}
    variant="outlined"
    size="large"
    sx={{
      backgroundColor: "white",
      "& .MuiOutlinedInput-root": {
        fontSize: "1.1rem",
        height: "56px",
      },
      flex: 1 // ensures equal width with camera select
    }}
    placeholder="Test ID (10367-1)"
  />

  {/* Camera Selection */}
  {availableCameras.length > 1 && (
    <FormControl
      fullWidth
      sx={{
        backgroundColor: "white",
        flex: 1, // equal width in mobile row
      }}
    >
      <InputLabel>Camera</InputLabel>
      <Select
        value={selectedCameraId}
        onChange={(e) => {
          setSelectedCameraId(e.target.value);
          if (isScanning) {
            stopCamera();
            setTimeout(() => startCamera(), 500);
          }
        }}
        label="Camera"
        size="large"
        sx={{
          "& .MuiOutlinedInput-root": {
            fontSize: "1.1rem",
            height: "56px",
          },
        }}
      >
        {availableCameras.map((camera, index) => (
          <MenuItem key={camera.deviceId} value={camera.deviceId}>
            {camera.label || `Camera ${index + 1}`}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )}
</Box>


      {/* Alert Messages - Keeping for backward compatibility, but hidden */}
      <div style={{ display: "none" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
      </div>

      {/* Camera/Image View */}
      <Paper
        elevation={3}
        sx={{
          flex: 1,
          minHeight: "250px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#000",
          position: "relative",
          overflow: "hidden",
          mb: 2,
        }}
      >
        {loading && (
          <Box
            sx={{
              textAlign: "center",
              color: "white",
              p: 3,
            }}
          >
            <CircularProgress sx={{ color: "white", mb: 2 }} />
            <Typography variant="h6" sx={{ opacity: 0.7 }}>
              {loadingType === "camera"
                ? "Requesting Camera Access..."
                : loadingType === "submit"
                  ? "Processing OCR Scan..."
                  : "Loading..."}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.5, mt: 1 }}>
              {loadingType === "camera"
                ? "Please allow camera permissions when prompted"
                : loadingType === "submit"
                  ? "Sending image and test ID to server..."
                  : "Please wait..."}
            </Typography>
          </Box>
        )}

        {!isScanning && !scannedImage && !loading && (
          <Box
            sx={{
              textAlign: "center",
              color: "white",
              p: 3,
            }}
          >
            <CameraAltIcon sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" sx={{ opacity: 0.7 }}>
              {authLoading
                ? "Loading..."
                : currentUser
                  ? "Ready to Scan"
                  : "Authentication Required"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.5, mt: 1 }}>
              {authLoading
                ? "Checking authentication status..."
                : currentUser
                  ? 'Click "Start Camera" to begin scanning documents'
                  : "Please sign in to access the OCR scanner"}
            </Typography>
          </Box>
        )}

        {isScanning && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            onCanPlay={() => {
              console.log("Video can play");
            }}
            onError={(e) => {
              console.error("Video error:", e);
              showSnackbar("Video playback error", "error");
            }}
          />
        )}

        {scannedImage && (
          <img
            src={scannedImage}
            alt="Scanned document"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        )}

        {/* Loading overlay for capture/submit */}
        {loading && (isScanning || scannedImage) && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress sx={{ color: "white" }} />
          </Box>
        )}
      </Paper>

      {/* Hidden canvas for image capture */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* Hidden file input for image upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/*"
        style={{ display: "none" }}
      />

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Camera active - show capture and upload buttons */}
        {!showButtons && isScanning && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<QrCodeScannerIcon />}
              onClick={captureImage}
              disabled={loading}
              sx={{
                flex: 4, // 80% width
                backgroundColor: "#0058a5",
                height: "56px",
                fontSize: "1.1rem",
                "&:hover": {
                  backgroundColor: "#004494",
                },
              }}
            >
              Capture Photo
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<UploadFileIcon />}
              onClick={triggerFileUpload}
              disabled={loading}
              sx={{
                flex: 1, // 20% width
                height: "56px",
                fontSize: "1.1rem",
                borderColor: "#0058a5",
                color: "#0058a5",
                "&:hover": {
                  borderColor: "#004494",
                  backgroundColor: "rgba(0, 88, 165, 0.04)",
                },
              }}
            >
              Upload
            </Button>
          </Box>
        )}

        {/* Show start camera and upload buttons when no camera is active */}
        {!showButtons && !isScanning && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<CameraAltIcon />}
              onClick={checkAuthenticationAndStart}
              disabled={loading || authLoading}
              sx={{
                flex: 4, // 80% width
                backgroundColor: "#0058a5",
                height: "56px",
                fontSize: "1.1rem",
                "&:hover": {
                  backgroundColor: "#004494",
                },
              }}
            >
              {loading
                ? "Starting Camera..."
                : authLoading
                  ? "Loading..."
                  : currentUser
                    ? "Start Camera"
                    : "Sign In to Start Camera"}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<UploadFileIcon />}
              onClick={triggerFileUpload}
              disabled={loading || authLoading || !currentUser}
              sx={{
                flex: 1, // 20% width
                height: "56px",
                fontSize: "1.1rem",
                borderColor: "#0058a5",
                color: "#0058a5",
                "&:hover": {
                  borderColor: "#004494",
                  backgroundColor: "rgba(0, 88, 165, 0.04)",
                },
              }}
            >
              Upload
            </Button>
          </Box>
        )}

        {/* After capture - show retry and submit buttons */}
        {showButtons && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              variant="outlined"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={retryScanning}
              disabled={loading}
              sx={{
                flex: 1,
                height: "56px",
                fontSize: "1.1rem",
                borderColor: "#0058a5",
                color: "#0058a5",
                "&:hover": {
                  borderColor: "#004494",
                  backgroundColor: "rgba(0, 88, 165, 0.04)",
                },
              }}
            >
              Retake Photo
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<SendIcon />}
              onClick={submitScan}
              disabled={loading}
              sx={{
                flex: 1,
                height: "56px",
                fontSize: "1.1rem",
                backgroundColor: "#0058a5",
                "&:hover": {
                  backgroundColor: "#004494",
                },
              }}
            >
              Scan
            </Button>
          </Box>
        )}
      </Box>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default OCR;
